/**
 * Giorni e ore del calendario come testo (AAAA-MM-GG, HH:MM), senza passare dal fuso del dispositivo:
 * l'API li manda gia' nell'ora di Roma, e il calendario li dispone cosi' come sono. I conti sui giorni
 * si fanno a mezzogiorno UTC, dove il cambio dell'ora legale non arriva.
 */

const MS_GIORNO = 86_400_000
const aMezzogiorno = (g: string) => Date.parse(`${g}T12:00:00Z`)
const comeGiorno = (ms: number) => new Date(ms).toISOString().slice(0, 10)

export const GIORNI_SETTIMANA = ['lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato', 'domenica']
export const GIORNI_CORTI = ['lun', 'mar', 'mer', 'gio', 'ven', 'sab', 'dom']
export const MESI = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto',
  'settembre', 'ottobre', 'novembre', 'dicembre']

/** Oggi a Roma. */
export const oggiARoma = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Rome' }).format(new Date())

export const piuGiorni = (g: string, n: number) => comeGiorno(aMezzogiorno(g) + n * MS_GIORNO)

/** Giorno ISO della settimana: 1 lunedi', 7 domenica. */
export const giornoSettimana = (g: string) => ((new Date(aMezzogiorno(g)).getUTCDay() + 6) % 7) + 1

/** Il lunedi' della settimana del giorno. */
export const lunediDi = (g: string) => piuGiorni(g, 1 - giornoSettimana(g))

/** I giorni da `dal` ad `al`, compresi. */
export function giorniFra(dal: string, al: string): string[] {
  const giorni: string[] = []
  for (let g = dal; g <= al; g = piuGiorni(g, 1)) giorni.push(g)
  return giorni
}

export const primoDelMese = (g: string) => `${g.slice(0, 7)}-01`
export function ultimoDelMese(g: string) {
  const [a, m] = g.split('-').map(Number)
  return comeGiorno(Date.UTC(a, m, 0, 12))
}
/** Lo stesso giorno del mese dopo (o prima), stretto all'ultimo del mese. */
export function piuMesi(g: string, n: number) {
  const [a, m, d] = g.split('-').map(Number)
  const primo = new Date(Date.UTC(a, m - 1 + n, 1, 12))
  const ultimo = new Date(Date.UTC(primo.getUTCFullYear(), primo.getUTCMonth() + 1, 0, 12)).getUTCDate()
  return comeGiorno(Date.UTC(primo.getUTCFullYear(), primo.getUTCMonth(), Math.min(d, ultimo), 12))
}

/** «martedì 10 novembre». */
export function giornoInParole(g: string, conAnno = false) {
  const [a, m, d] = g.split('-').map(Number)
  return `${GIORNI_SETTIMANA[giornoSettimana(g) - 1]} ${d} ${MESI[m - 1]}${conAnno ? ` ${a}` : ''}`
}
/** «mar 10». */
export const giornoCorto = (g: string) => `${GIORNI_CORTI[giornoSettimana(g) - 1]} ${Number(g.slice(8, 10))}`
/** «novembre 2026». */
export const meseInParole = (g: string) => `${MESI[Number(g.slice(5, 7)) - 1]} ${g.slice(0, 4)}`

/** «10-14 novembre 2026», «28 ottobre - 2 novembre 2026». */
export function periodoInParole(dal: string, al: string) {
  const [ad, md, gd] = dal.split('-').map(Number)
  const [aa, ma, ga] = al.split('-').map(Number)
  if (dal === al) return giornoInParole(dal, true)
  if (ad === aa && md === ma) return `${gd}-${ga} ${MESI[ma - 1]} ${aa}`
  if (ad === aa) return `${gd} ${MESI[md - 1]} - ${ga} ${MESI[ma - 1]} ${aa}`
  return `${gd} ${MESI[md - 1]} ${ad} - ${ga} ${MESI[ma - 1]} ${aa}`
}

/** HH:MM in minuti dalla mezzanotte; «24:00» vale 1440. */
export const minuti = (ora: string) => Number(ora.slice(0, 2)) * 60 + Number(ora.slice(3, 5))
/** Minuti dalla mezzanotte in HH:MM. */
export const ora = (min: number) =>
  `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`
